import { yupResolver } from '@hookform/resolvers/yup';
import {
  CreateInvitationDto,
  Invitation,
  InvitationTemplateEnum,
  RoleEnum,
  UpdateInvitationDto,
  Workspace,
} from '@sr-sdks/api-sdk-axios';
import { Button, sonner } from '@sr/ui-library';
import { useEffect } from 'react';
import { Input } from 'react-daisyui';
import { Controller, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import {
  SEND_INVITE_FAILED_MESSAGE,
  SEND_INVITE_SUCCESS_MESSAGE,
} from '../constant/Common';
import { invitationService } from '../reducers/permissions/invitationSlice';
import { userService } from '../reducers/permissions/userSlice';
import { SendInviteFormSchema } from '../schema/SendInviteFormSchema';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import {
  getCurrentUserFullName,
  isLoadingState,
} from '../utils/helpers/common';
import AccessLevelDropdown from './AccessLevelDropdown';
import StaffAccessLevelDropdown from './StaffAccessLevelDropdown';

interface IFormInput {
  accessLevel: RoleEnum;
  email: string;
}

interface ISendInviteFormProps {
  accessLevelDefaultValue?: RoleEnum;
  invitation?: Invitation;
  invitationType?: InvitationTemplateEnum;
  isPermittedToChangeWorkspaceOwner?: boolean;
  isReadOnly?: boolean;
  onInviteFailed?: () => void;
  onInviteSuccess?: () => void;
  workspace?: Workspace;
}

const { toast } = sonner;

const SendInviteForm = ({
  accessLevelDefaultValue,
  invitation,
  invitationType = InvitationTemplateEnum.CustomerInvitation as InvitationTemplateEnum,
  isPermittedToChangeWorkspaceOwner = false,
  isReadOnly = false,
  onInviteFailed,
  onInviteSuccess,
  workspace,
}: ISendInviteFormProps) => {
  const dispatch = useAppDispatch();

  const isStaffInvitation =
    invitationType === InvitationTemplateEnum.StaffInvitation;

  const isCreating = useTypedSelector(invitationService.selectors.isCreating);
  const isResending = useTypedSelector(invitationService.selectors.isResending);

  const createError = useTypedSelector(invitationService.selectors.createError);
  const resendError = useTypedSelector(invitationService.selectors.resendError);

  const userMe = useTypedSelector(userService.selectors.userMe);

  const defaultRoleForStaffInvitation = isStaffInvitation
    ? RoleEnum.CustomerSupportUser
    : RoleEnum.WorkspaceUser;

  const accessLevelDefault =
    accessLevelDefaultValue ?? defaultRoleForStaffInvitation;

  const isSubmitting =
    isLoadingState(isCreating) || isLoadingState(isResending);

  const isAccessLevelVisible =
    !invitation ||
    invitation.invitationTemplate === InvitationTemplateEnum.CustomerInvitation;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<IFormInput>({
    defaultValues: {
      accessLevel: accessLevelDefault,
      email: '',
    },
    mode: 'onChange',
    resolver: yupResolver(SendInviteFormSchema),
  });

  useEffect(() => {
    if (!invitation?.emailAddress) return;

    reset({
      accessLevel: invitation.userRoles[0],
      email: invitation.emailAddress,
    });

    dispatch(invitationService.actions.resetErrors());
  }, [invitation?.emailAddress, reset]);

  const resendInvitation = async (
    id: string,
    updateInvitationDto: UpdateInvitationDto,
  ) => {
    const resendInvitationResponse = await dispatch(
      invitationService.resend({
        id,
        updateInvitationDto,
      }),
    );

    if (invitationService.resend.fulfilled.match(resendInvitationResponse)) {
      onInviteSuccess?.();
      toast.success(SEND_INVITE_SUCCESS_MESSAGE);
      reset();
    } else {
      onInviteFailed?.();
      toast.error(SEND_INVITE_FAILED_MESSAGE);
    }
  };

  const submitForm = async (formData: IFormInput) => {
    const workspaceName = workspace?.workspaceName ?? invitation?.workspaceName;

    if (invitation) {
      return resendInvitation(invitation._id, {
        emailAddress: formData.email,
        invitationTemplate: invitation.invitationTemplate,
        inviter: userMe ? getCurrentUserFullName(userMe) : undefined,
        userRoles: [formData.accessLevel],
        workspaceName: workspaceName,
      } as UpdateInvitationDto);
    }

    const payload: CreateInvitationDto = {
      emailAddress: formData.email,
      invitationTemplate: invitationType,
      inviter: userMe ? getCurrentUserFullName(userMe) : undefined,
      userRoles: [formData.accessLevel],
      workspaceId: workspace?._id ?? '',
      workspaceName: workspaceName,
    };

    const createInvitation = await dispatch(invitationService.create(payload));

    if (invitationService.create.fulfilled.match(createInvitation)) {
      onInviteSuccess?.();
      toast.success(SEND_INVITE_SUCCESS_MESSAGE);
      reset();
    } else {
      onInviteFailed?.();
      toast.error(SEND_INVITE_FAILED_MESSAGE);
    }
  };

  return (
    <div className="grid-flow-row">
      <span className="text-base font-normal text-sr-black opacity-50">
        Invite valid for 21 days.
      </span>
      <div className="mt-5 gap-10">
        <form className="relative w-full" onSubmit={handleSubmit(submitForm)}>
          <div className="flex flex-col gap-5 md:flex-row">
            <div className="basis-full md:basis-3/4">
              <label className="sr-only" htmlFor="id-for-emailaddress">
                Email
              </label>
              <div
                className={twMerge(
                  'grid space-x-5',
                  isAccessLevelVisible && 'grid-cols-[1fr_135px]',
                )}
              >
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="id-for-emailaddress"
                      name="email"
                      color={errors.email?.message ? 'error' : undefined}
                      placeholder="Email"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        dispatch(invitationService.actions.resetErrors());
                      }}
                    />
                  )}
                />
                {isAccessLevelVisible && (
                  <Controller
                    name="accessLevel"
                    control={control}
                    render={({ field }) =>
                      isStaffInvitation ? (
                        <StaffAccessLevelDropdown
                          selectedRoleKey={field.value}
                          onRoleSelect={(roleKey) => {
                            field.onChange(roleKey);
                          }}
                        />
                      ) : (
                        <AccessLevelDropdown
                          isPermittedToChangeWorkspaceOwner={
                            isPermittedToChangeWorkspaceOwner
                          }
                          isReadOnly={isReadOnly}
                          selectedRoleKey={field.value}
                          variant="outline"
                          onRoleSelect={(roleKey) => field.onChange(roleKey)}
                        />
                      )
                    }
                  />
                )}
              </div>
            </div>
            <div className="basis-full content-center md:basis-1/4">
              <Button
                className="w-full"
                disabled={
                  errors.email?.message != null ||
                  createError?.statusCode === 400 ||
                  resendError?.statusCode === 400 ||
                  isSubmitting
                }
                loading={isSubmitting}
                size="lg"
                type="submit"
              >
                {invitation ? 'Resend' : 'Send'} Invite
              </Button>
            </div>
          </div>
          {(errors.email?.message ||
            createError?.statusCode === 400 ||
            resendError?.statusCode === 400) && (
            <div
              id="id-for-emailaddress-errorMessage"
              aria-live="polite"
              className="label"
            >
              <span className="label-text-alt text-error">
                {errors.email?.message != null
                  ? errors.email?.message
                  : createError?.statusCode === 400 ||
                      resendError?.statusCode === 400
                    ? 'Email address you entered has been invited or is already a Teammate.'
                    : ''}
              </span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SendInviteForm;
